import type { ReactNode } from 'react';
import { cx } from '../cx';
import { type ChartLayout } from './layout';
import { LABEL_TEXT, VALUE_TEXT } from './scale';

export type FunnelStage = {
  id: string;
  label: ReactNode;
  value: number;
  display: ReactNode;
  /** The share that went on from the stage before, in words ("33% of applied"); empty for the first. */
  conversion?: ReactNode;
};

export type FunnelProps = {
  /** In order, widest first. */
  stages: FunnelStage[];
  /** `auto` (default) switches to the narrow form in a narrow container: each stage label above its bar. */
  layout?: ChartLayout;
  /** One sentence with every stage, for screen readers. */
  description: string;
  className?: string;
};

const ROW: Record<ChartLayout, string> = {
  auto: 'flex flex-col gap-0.5 @md:flex-row @md:items-center @md:gap-3',
  narrow: 'flex flex-col gap-0.5',
  wide: 'flex items-center gap-3',
};
const ROW_LABEL: Record<ChartLayout, string> = {
  auto: '@md:w-1/4 @md:shrink-0',
  narrow: '',
  wide: 'w-1/4 shrink-0',
};
// In a column, flex-1 would collapse the bar line to no height: the narrow form sizes it by width.
const BAR_LINE: Record<ChartLayout, string> = { auto: '@md:flex-1', narrow: '', wide: 'flex-1' };

/**
 * Stages that narrow, on one left edge: the reader's own pipeline (saved, applied, interview,
 * offer). The reader's data is ink; the last stage is sun. Never a trapezoid.
 */
export function Funnel({ stages, layout = 'auto', description, className }: FunnelProps) {
  const top = Math.max(1, stages[0]?.value ?? 1);
  return (
    <div role="img" aria-label={description} className={cx('@container flex flex-col gap-2', className)}>
      {stages.map((s, i) => (
        <div key={s.id} className={ROW[layout]}>
          <span className={cx('truncate', LABEL_TEXT, ROW_LABEL[layout])}>{s.label}</span>
          <span className={cx('flex min-w-0 items-center gap-2', BAR_LINE[layout])}>
            <span
              className={cx(
                'block h-6 min-w-1.5 origin-left rounded-jc-info motion-safe:animate-jc-grow-x',
                i === stages.length - 1 ? 'bg-jc-chart-2' : 'bg-jc-ink',
              )}
              style={{ width: `calc((100% - 9rem) * ${s.value / top})` }}
            />
            <span className={VALUE_TEXT}>{s.display}</span>
            {s.conversion ? <span className="truncate text-xs text-jc-ink-secondary">{s.conversion}</span> : null}
          </span>
        </div>
      ))}
    </div>
  );
}
